import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import innatesMiddlewareService from '../services/innatesMiddlewareService';
import { 
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const Profile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadProfileData();
  }, [user]);

  const loadProfileData = async () => {
    if (!user?.email) return;

    try {
      setLoading(true);

      // Get user data from user table
      const { data: userData, error } = await supabase
        .from('user')
        .select('*')
        .eq('email', user.email)
        .single();

      if (error) {
        console.error('Error loading profile:', error);
        return;
      }

      // The user table keeps the phone in "mobile" and links to an address row.
      let addressLine = '';
      if (userData.address) {
        const { data: addressRow } = await supabase
          .from('address')
          .select('addressLine1')
          .eq('id', userData.address)
          .maybeSingle();
        addressLine = addressRow?.addressLine1 || '';
      }

      setProfileData({ ...userData, addressLine });
      setFormData({
        name_first: userData.name_first || '',
        name_last: userData.name_last || '',
        phone: userData.mobile || '',
        address: addressLine,
        imageProfileURL: userData.imageProfileURL || ''
      });

    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const { error } = await supabase
        .from('user')
        .update({
          name_first: formData.name_first,
          name_last: formData.name_last,
          mobile: formData.phone,
          imageProfileURL: formData.imageProfileURL
        })
        .eq('email', user.email);

      if (error) {
        console.error('Error updating profile:', error);
        alert('Failed to update profile');
        return;
      }

      // The address text lives in the linked address row
      if (profileData?.address && formData.address !== (profileData.addressLine || '')) {
        const { error: addressError } = await supabase
          .from('address')
          .update({ addressLine1: formData.address })
          .eq('id', profileData.address);
        if (addressError) console.warn('⚠️ Could not update address:', addressError.message);
      }

      // Sync to Innates middleware (non-blocking)
      try {
        // No middleware number on our record: look the farmer up in the
        // middleware's own list (API 13) and restore it.
        let middlewareId = profileData?.middleware_id;
        if (!middlewareId && profileData?.id) {
          const record = await innatesMiddlewareService.lookupRecord('user', profileData.id);
          if (record?.found && !record.isDeleted) {
            middlewareId = record.id;
            await supabase.from('user').update({ middleware_id: middlewareId }).eq('id', profileData.id);
          }
        }

        if (middlewareId) {
          await innatesMiddlewareService.updateFarmer({
            farmerId: String(profileData.id),
            name: `${formData.name_first} ${formData.name_last}`,
            phone: formData.phone || '',
            icNo: profileData.identification_no || '',
            id: middlewareId
          });
          console.log('✅ Profile synced to Innates middleware');
        } else {
          console.log('ℹ️ No middleware_id found, skipping middleware sync');
        }
      } catch (middlewareErr) {
        console.warn('⚠️ Middleware sync failed (non-blocking):', middlewareErr.message);
      }

      await loadProfileData();
      setEditing(false);
      alert('Profile updated successfully!');

    } catch (err) {
      console.error('Failed to save profile:', err);
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name_first: profileData.name_first || '',
      name_last: profileData.name_last || '',
      phone: profileData.mobile || '',
      address: profileData.addressLine || '',
      imageProfileURL: profileData.imageProfileURL || ''
    });
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PencilIcon className="h-4 w-4" />
            <span>Edit Profile</span>
          </button>
        ) : (
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCancel}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              <XMarkIcon className="h-4 w-4" />
              <span>Cancel</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <CheckIcon className="h-4 w-4" />
              <span>{saving ? 'Saving...' : 'Save'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Profile Card */}
      <div className="card">
        <div className="p-6">
          {/* Profile Picture */}
          <div className="flex items-center space-x-6 mb-8">
            <div className="relative">
              {formData.imageProfileURL ? (
                <img
                  src={formData.imageProfileURL}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                  <UserCircleIcon className="w-16 h-16 text-gray-400" />
                </div>
              )}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {profileData?.name_first} {profileData?.name_last}
              </h2>
              <p className="text-gray-500">{user?.email}</p>
              <p className="text-sm text-gray-400 mt-1">
                Member since {profileData?.created_at ? new Date(profileData.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>

          {/* Profile Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserCircleIcon className="inline h-4 w-4 mr-2" />
                First Name
              </label>
              {editing ? (
                <input
                  type="text"
                  value={formData.name_first}
                  onChange={(e) => setFormData({ ...formData, name_first: e.target.value })}
                  className="input w-full"
                />
              ) : (
                <p className="text-gray-900">{profileData?.name_first || 'Not set'}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserCircleIcon className="inline h-4 w-4 mr-2" />
                Last Name
              </label>
              {editing ? (
                <input
                  type="text"
                  value={formData.name_last}
                  onChange={(e) => setFormData({ ...formData, name_last: e.target.value })}
                  className="input w-full"
                />
              ) : (
                <p className="text-gray-900">{profileData?.name_last || 'Not set'}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <EnvelopeIcon className="inline h-4 w-4 mr-2" />
                Email
              </label>
              <p className="text-gray-900">{user?.email}</p>
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <PhoneIcon className="inline h-4 w-4 mr-2" />
                Phone
              </label>
              {editing ? (
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input w-full"
                  placeholder="+60123456789"
                />
              ) : (
                <p className="text-gray-900">{profileData?.mobile || 'Not set'}</p>
              )}
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPinIcon className="inline h-4 w-4 mr-2" />
                Address
              </label>
              {editing ? (
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input w-full"
                  rows="3"
                  placeholder={profileData?.address ? 'Enter your address' : 'No address on record'}
                  disabled={!profileData?.address}
                />
              ) : (
                <p className="text-gray-900">{profileData?.addressLine || 'Not set'}</p>
              )}
            </div>

            {/* Profile Image URL */}
            {editing && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Image URL
                </label>
                <input
                  type="url"
                  value={formData.imageProfileURL}
                  onChange={(e) => setFormData({ ...formData, imageProfileURL: e.target.value })}
                  className="input w-full"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">User ID</p>
                <p className="text-gray-900 font-mono text-sm">{profileData?.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  <CalendarIcon className="inline h-4 w-4 mr-1" />
                  Account Created
                </p>
                <p className="text-gray-900">
                  {profileData?.created_at ? new Date(profileData.created_at).toLocaleString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
